import { useRef, useEffect } from "react";
import Swiper from "swiper";
import { A11y, Navigation, Pagination } from "swiper/modules";
import { CloudinaryAsset } from "@models/cloudinary-asset.model";
import { PartnershipType } from "@models/partnership.model";
import { ReactComponent as CaretBackSVG } from "@images/caret_back.svg";
import DetailVideo from "../detail-video";
import { Container, Controls, Slide, SlideWrap, Slider } from "./DetailHero.styled";

export interface DetailHeroProps {
  media: CloudinaryAsset[];
  type?: string;
}

const padNumber = (num: number) => {
  return num < 10 ? `0${num}` : num;
};

const DetailHero = ({ media, type }: DetailHeroProps) => {
  const swiperElRef = useRef<HTMLDivElement | undefined>();
  const swiperInstanceRef = useRef<Swiper | undefined>();

  useEffect(() => {
    if (!swiperElRef.current) return;
    if (media.length > 1) {
      swiperInstanceRef.current = new Swiper(swiperElRef.current, {
        modules: [A11y, Navigation, Pagination],
        loop: true,
        slidesPerView: 1,
        a11y: {
          prevSlideMessage: "Previous press slide",
          nextSlideMessage: "Next press slide",
        },
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        pagination: {
          el: ".swiper-pagination",
          type: "custom",

          renderCustom: (swiper, current, total) => {
            return `
              <div class="swiper-pagination-item">
                <span>
                  <span>${padNumber(current)}</span>/<span>${padNumber(total)}</span>
                </span>
              </div>
            `;
          },
        },
      });
    }

    return () => {
      if (swiperInstanceRef.current) {
        swiperInstanceRef.current.destroy();
      }
    };
  }, []);

  return (
    <Container>
      <Slider>
        <div className="swiper-container" ref={swiperElRef}>
          <div className="swiper-wrapper">
            {media?.map(({ url, alt, format }, idx) => {
              if (!url) return null;
              return (
                <div className="swiper-slide" key={idx}>
                  <Slide>
                    <SlideWrap cover={type === PartnershipType}>
                      {format === "video" ? (
                        <DetailVideo src={url} />
                      ) : (
                        <img src={url} alt={alt} loading="lazy" />
                      )}
                    </SlideWrap>
                  </Slide>
                </div>
              );
            })}
          </div>

          {media.length > 1 && (
            <Controls>
              <div className="grid-container">
                <div className="grid-x grid-padding-x">
                  <div className="cell small-12 large-10 large-offset-2">
                    <button className="swiper-button-prev">
                      <CaretBackSVG />
                    </button>

                    <div className="swiper-pagination"></div>

                    <button className="swiper-button-next">
                      <CaretBackSVG />
                    </button>
                  </div>
                </div>
              </div>
            </Controls>
          )}
        </div>
      </Slider>
    </Container>
  );
};

export default DetailHero;
